import { Translation } from "react-i18next";
import { inject } from "mobx-react";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useHistory } from "react-router";
import * as Webapi from "@ihr-radioedit/inferno-webapi";

import { resolveMenu } from "../../../core/lib/menu";
import { reverseRoute } from "../../../core/lib/route";
import { resizeWatcher } from "../../../core/lib/resize-watcher";
import { SocialNetworkData } from "../../../core/lib/social";
import { addContests } from "../../../core/lib/contests";
import { slugify } from "../../../core/lib/utilities";
import { isWindowDefined } from "../../../core/lib/window";
import { getContests } from "@ihr-radioedit/inferno-core";
import { SitesMenu } from "../../../core/chrome/Navigation.component";
import { MagicLink } from "../../../core/ui";
import { ArrowIcon } from "../../../core/components/icons/ArrowIcon.component";
import { DropDownIcon } from "../../../core/components/icons/DropDownIcon.component";
import { PremiereLeavingSiteButton } from "./PremiereLeavingSiteButton.component";

import styles from "./PremiereNav.module.scss";
import { Store } from "../../../../client/stores";

interface PremiereNavProps {
    store?: Store;
    menus: SitesMenu[] | null;
    sections: Webapi.UserConfigFragment;
    social?: SocialNetworkData[] | null;
}

export const PremiereNavMenuItem = ({ menu, context }: { menu: SitesMenu; context?: string }) => {
    return menu.type && menu.type === "link_warning" ? (
        <PremiereLeavingSiteButton url={menu.href ?? ""} label={menu.label ?? ""} type={menu.type ?? ""} />
    ) : (
        <Translation>
            {t => (
                <MagicLink
                    className={styles.menuLabel}
                    to={menu.href || ""}
                    label={menu.label || ""}
                    target={menu.target || ""}
                    context={context || menu.context}
                >
                    {t(`${menu.label}`)}
                </MagicLink>
            )}
        </Translation>
    );
};

export const PremiereNav = inject("store")(({ store, sections, menus }: PremiereNavProps) => {
    if (!store || !menus) {
        return null;
    }

    const navBackgroundColor = sections.design?.navigation_color;
    const navLinksColor = sections.design?.navigation_links_color;
    const navStyles = `
        :root {
            ${navBackgroundColor ? ` --premiere-navigation-background-color: ${navBackgroundColor};` : ""}
            ${navLinksColor ? ` --premiere-navigation-links-color: ${navLinksColor};` : ""}
        }`;

    const history = useHistory();
    const navListEl = useRef<HTMLUListElement>(null);

    const brandIds = useMemo(() => {
        const brandIdss: { [key: string]: string } = {};
        store?.site.micrositeReferences.forEach(index => (brandIdss[`/index${index._id}`] = index.slug));
        return brandIdss;
    }, [store?.site.micrositeReferences]);

    const [resolvedMenus, setResolvedMenus] = useState(
        menus?.map(menu => resolveMenu(menu, store?.env.MICROSITE_ROOT, null, store?.site, brandIds)),
    );
    const [menuTop, setMenuTop] = useState(0);
    const [longNavigation, setLongNavigation] = useState(false);
    const [isVisible, setVisibility] = useState("");
    const [showMore, setShowMore] = useState(store.stackedNavState);

    const toggleDropdownMenu = (label: string) => {
        if (!isVisible || isVisible !== label) {
            setVisibility(label);
        } else {
            setVisibility("");
        }
    };

    const toggleMore = () => {
        store.storeStackedNavState(!showMore);
        setShowMore(!showMore);

        if (!!isVisible) {
            setVisibility("");
        }
    };

    const handleWindowResize = () => {
        // We have to dyanmically calculate the length of our child items.
        // If they are longer than the parent element we need to show the "MORE" button.
        const menuPosition = navListEl?.current?.getBoundingClientRect();

        if (!!menuPosition) {
            setMenuTop(Math.floor(menuPosition.top));
        }

        let menuItemsLength = 0;
        const menuWidth = navListEl?.current?.offsetWidth;

        if (!!menuWidth) {
            menuItemsLength = Object.values(navListEl?.current!.childNodes).reduce(
                (total: number, i: any) => total + i.offsetWidth,
                -2,
            );
        }

        if (menuWidth! < menuItemsLength) {
            setLongNavigation(true);
        } else {
            setLongNavigation(false);
        }
    };

    const resetMenus = useCallback(() => {
        store.storeStackedNavState(false);
        setShowMore(false);
        setVisibility("");
    }, [store]);

    const loadMenus = useCallback(async () => {
        // At some point, we should refactor this
        // and break out the contest links from the rest of the menu items. - SR
        // TODO: IHRAL-8248
        let menuItems = menus?.map(menu => resolveMenu(menu, store?.env.MICROSITE_ROOT, null, store?.site, brandIds));

        if (store?.site.sections.navigation?.contest_nav_switch) {
            const contests = await getContests(
                { accountId: store.site.index.slug },
                store.tags.surrogateKeys,
                store.getSdkOpts(),
            );
            const contestMenu = addContests(
                Array.isArray(contests) ? contests.slice(0, 5) : [],
                menuItems,
                store,
                brandIds,
            );
            menuItems = [...contestMenu];
        }
        setResolvedMenus(menuItems);
        handleWindowResize();
    }, [store, brandIds, menus]);

    useEffect(() => {
        if (isWindowDefined()) {
            handleWindowResize();
            resizeWatcher.onWidthChange.subscribe(handleWindowResize);
            return () => resizeWatcher.onWidthChange.unsubscribe(handleWindowResize);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        history.listen(resetMenus);
        if (isWindowDefined()) {
            loadMenus();
        }
    }, [history, loadMenus, resetMenus]);

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: navStyles }} />
            <nav className={`${styles.horizontalNav} ${showMore ? "stacked" : ""}`}>
                <div className={`${styles.outerWrap} ${isVisible ? "visible" : ""}`}>
                    {!!resolvedMenus.length ? (
                        <ul
                            className={styles.mainNav}
                            ref={navListEl}
                            style={{
                                maxHeight: `calc(100vh - ${menuTop}px)`,
                            }}
                        >
                            {resolvedMenus.map((menuItem: SitesMenu, index: number) => (
                                <li className={styles.topLabel} key={`${menuItem.label}_${menuItem.id}`}>
                                    {!menuItem.children?.length ? (
                                        <PremiereNavMenuItem
                                            menu={menuItem}
                                            context={menuItem.label ? `${slugify(menuItem.label)}_${index}` : undefined}
                                        />
                                    ) : (
                                        <>
                                            <Translation>
                                                {t => (
                                                    <button
                                                        className={styles.menuLabel}
                                                        onClick={() => toggleDropdownMenu(menuItem.label!)}
                                                    >
                                                        {t(`${menuItem.label}`)}
                                                        <span className={styles.icon}>
                                                            {isVisible === menuItem.label ? (
                                                                <DropDownIcon direction="up" />
                                                            ) : (
                                                                <DropDownIcon direction="down" />
                                                            )}
                                                        </span>
                                                    </button>
                                                )}
                                            </Translation>
                                            <div className={styles.dropdownOverlay}>
                                                <ul
                                                    className={`${styles.submenu} ${
                                                        isVisible === menuItem.label ? "visible" : ""
                                                    }`}
                                                    aria-hidden={!!isVisible}
                                                    style={{
                                                        maxHeight: `calc(100vh - ${menuTop + 100}px)`,
                                                    }}
                                                >
                                                    {menuItem.children?.map(childMenuItem => (
                                                        <li key={`${childMenuItem.label}_${childMenuItem.id}`}>
                                                            <PremiereNavMenuItem
                                                                menu={childMenuItem}
                                                                context={
                                                                    childMenuItem.label
                                                                        ? `${slugify(childMenuItem.label)}_${index}`
                                                                        : undefined
                                                                }
                                                            />
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </>
                                    )}
                                </li>
                            ))}
                            {sections.navigation?.contact_menu_switch ? (
                                <li className={styles.topLabel}>
                                    <Translation>
                                        {t => (
                                            <MagicLink
                                                className={styles.menuLabel}
                                                to={reverseRoute(store.site, "contact") || ""}
                                                context="contact"
                                            >
                                                {t("contact")}
                                            </MagicLink>
                                        )}
                                    </Translation>
                                </li>
                            ) : null}
                            {sections.partners?.postup_switch ? (
                                <li className={styles.topLabel}>
                                    <Translation>
                                        {t => (
                                            <MagicLink
                                                className={styles.menuLabel}
                                                to={reverseRoute(store.site, "newsletter") || ""}
                                                context="newsletter"
                                            >
                                                {t("newsletter")}
                                            </MagicLink>
                                        )}
                                    </Translation>
                                </li>
                            ) : null}
                            {sections.navigation?.advertise_switch ? (
                                <li className={styles.topLabel}>
                                    <Translation>
                                        {t => (
                                            <MagicLink
                                                className={styles.menuLabel}
                                                to={reverseRoute(store.site, "advertise") || ""}
                                                context="advertise"
                                            >
                                                {t("advertise_with_us", {
                                                    name: store.site.sections.general?.name,
                                                })}
                                            </MagicLink>
                                        )}
                                    </Translation>
                                </li>
                            ) : null}
                            {longNavigation ? (
                                <li aria-hidden="true">
                                    <span style={{ width: "100px" }}>
                                        <p
                                            style={{ width: 0, height: 0, fontSize: 0, color: "transparent" }}
                                            aria-hidden="true"
                                        >
                                            This is a spacer to push nav content over when part of the nav is hidden
                                            behind the "MORE" button
                                        </p>
                                    </span>
                                </li>
                            ) : null}
                        </ul>
                    ) : null}
                    {longNavigation ? (
                        <button className={`${styles.menuLabel} ${styles.moreTrigger}`} onClick={toggleMore}>
                            {showMore ? (
                                <span className={styles.icon}>
                                    Less <ArrowIcon direction="up" />
                                </span>
                            ) : (
                                <span className={styles.icon}>
                                    More <ArrowIcon direction="down" />
                                </span>
                            )}
                        </button>
                    ) : null}
                </div>
            </nav>
        </>
    );
});
